export const QUICK_ADD_WORKSPACE_OPTIONS = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'free-users', label: 'Free Users' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'communication', label: 'Prospects' },
  { value: 'guide-partners', label: 'Guide Partners' },
  { value: 'advisory-partners', label: 'Advisory Partners' },
  { value: 'support-partners', label: 'Support Partners' },
  { value: 'super-fans', label: 'Super Fans' },
  { value: 'heart-work', label: 'Dad Partners' },
  { value: 'stewardship', label: 'Operations' },
];

export const QUICK_ADD_CATEGORY_OPTIONS = [
  { value: 'launch', label: 'Launch' },
  { value: 'people', label: 'People' },
  { value: 'content', label: 'Content' },
  { value: 'system', label: 'System' },
  { value: 'revenue', label: 'Revenue' },
];

export const QUICK_ADD_URGENCY_OPTIONS = [
  { value: 'critical', label: 'Critical', score: 95 },
  { value: 'high', label: 'High', score: 75 },
  { value: 'medium', label: 'Medium', score: 55 },
  { value: 'low', label: 'Low', score: 30 },
];

export const QUICK_ADD_TIMING_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'later', label: 'Later' },
  { value: 'none', label: 'No due date' },
];

export const QUICK_ADD_POSITION_OPTIONS = [
  { value: 'top3', label: 'Top 3 now' },
  { value: 'active', label: 'Active queue' },
  { value: 'later', label: 'Later' },
];

const KEYWORD_RULES = [
  {
    match: ['upcoming event', 'upcoming events'],
    workspace: 'stewardship',
    category: 'launch',
    urgency: 'high',
    timing: 'today',
    position: 'top3',
    reason: 'Upcoming events belong in Operations and usually need near-term action.',
    targetRoomId: 'system-health',
    targetTab: 'sitesettings',
    targetAnchor: 'upcoming-events',
  },
  {
    match: ['guide partner outreach', 'guide partners outreach', 'guest guide outreach'],
    workspace: 'guide-partners',
    category: 'people',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'This maps directly to the Guide Partner outreach workflow.',
    targetRoomId: 'guide-partners',
    targetTab: 'guestguides',
    targetAnchor: 'guide-partner-outreach',
  },
  {
    match: ['monitor', 'incident', 'alert', 'outage', 'health', 'watcher', 'production', 'uptime', 'deploy'],
    workspace: 'monitoring',
    category: 'system',
    urgency: 'high',
    timing: 'today',
    position: 'top3',
    reason: 'System wording suggests operator attention.',
    targetRoomId: 'system-health',
    targetTab: 'monitoring',
    targetAnchor: 'production',
  },
  {
    match: ['launch', 'founding 40', 'webinar', 'event', 'zoom'],
    workspace: 'communication',
    category: 'launch',
    urgency: 'high',
    timing: 'today',
    position: 'top3',
    reason: 'Launch-sensitive wording usually belongs in the prospects lane first.',
  },
  {
    match: ['member', 'dad', 'feedback', 'support', 'resource', 'recording', 'peer support'],
    workspace: 'heart-work',
    category: 'people',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'Dad-facing support work usually belongs in Dad Partners.',
  },
  {
    match: ['guide', 'guest guide', 'invite', 'schedule', 'sequence', 'onboarding'],
    workspace: 'guide-partners',
    category: 'people',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'Guide Partner workflow terms suggest queue work there.',
  },
  {
    match: ['advisory', 'mission pulse', 'advisory ask', 'observations thread'],
    workspace: 'advisory-partners',
    category: 'content',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'Advisory-specific wording suggests the private advisory lane.',
    targetRoomId: 'advisory-partners',
    targetTab: 'advisorycontent',
  },
  {
    match: ['price', 'revenue', 'billing', 'coupon', 'analytics', 'retention', 'site settings', 'document'],
    workspace: 'stewardship',
    category: 'revenue',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'Business or upkeep terms suggest Operations.',
  },
  {
    match: ['email', 'guest pass', 'outreach', 'campaign', 'dad outreach', 'referral'],
    workspace: 'communication',
    category: 'people',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'Outreach wording suggests the prospects lane.',
  },
];

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function getUrgencyOption(value) {
  return QUICK_ADD_URGENCY_OPTIONS.find((option) => option.value === value) || QUICK_ADD_URGENCY_OPTIONS[2];
}

export function buildSuggestedDueAt(timing) {
  const now = new Date();
  if (timing === 'today') {
    const due = new Date(now);
    due.setHours(17, 0, 0, 0);
    return due.toISOString();
  }
  if (timing === 'this_week') {
    const due = new Date(now);
    due.setDate(due.getDate() + 3);
    due.setHours(17, 0, 0, 0);
    return due.toISOString();
  }
  return null;
}

export function buildQuickAddSuggestions({ title, detail }) {
  const text = `${normalizeText(title)} ${normalizeText(detail)}`.trim();
  const defaultSuggestion = {
    workspace: 'communication',
    category: 'people',
    urgency: 'medium',
    timing: 'this_week',
    position: 'active',
    reason: 'Suggested for the priority board from the quick-add note.',
  };

  const matchedRule = KEYWORD_RULES.find((rule) => rule.match.some((term) => text.includes(term)));
  const suggestion = matchedRule || defaultSuggestion;
  const urgencyOption = getUrgencyOption(suggestion.urgency);
  const aiPriorityScore = urgencyOption.score + (suggestion.position === 'top3' ? 10 : suggestion.position === 'later' ? -15 : 0);

  return {
    workspace: suggestion.workspace,
    category: suggestion.category,
    urgency: suggestion.urgency,
    timing: suggestion.timing,
    position: suggestion.position,
    urgencyScore: urgencyOption.score,
    aiPriorityScore,
    dueAt: buildSuggestedDueAt(suggestion.timing),
    rankReason: suggestion.reason,
    targetRoomId: suggestion.targetRoomId || null,
    targetTab: suggestion.targetTab || null,
    targetAnchor: suggestion.targetAnchor || null,
  };
}
